import UploadIcon from "@mui/icons-material/Upload"
import {
	Alert,
	Button,
	CircularProgress,
	Grid,
	Paper,
	Snackbar,
	TextField,
	Typography,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { AxiosResponse } from "axios"
import { MuiColorInput, MuiColorInputValue } from "mui-color-input"
import { ChangeEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { ReactComponent as SettingSvg } from "../../../assets/img/settings.svg"
import { AppDispatch } from "../../../store"
import { BankState, updateBank } from "../../../store/actions/bank"

interface ICreateFormProps {
	isEditMode?: boolean
	bank?: BankState | undefined
	onClose: () => void
}

const CreateForm = ({ isEditMode, bank }: ICreateFormProps) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const [alertMessage, setAlertMessage] = useState<string | undefined>(
		undefined
	)
	const [isErrorSnackBarOpen, setIsErrorSnackBarOpen] = useState<boolean>(false)
	const [isSavingForm, setIsSavingForm] = useState<boolean>(false)
	const [isSaved, setIsSaved] = useState<boolean>(false)
	const [formData, setFormData] = useState<BankState>({
		autoAccept: false | undefined,
		bankContact: undefined,
		bankId: "",
		bankImage: undefined,
		bankName: "",
		checkIban: false,
		createdBy: "",
		creationDate: "",
		deleted: false,
		enabled: true,
		id: 0,
		modificationDate: undefined,
		modifiedBy: undefined,
		primaryColor: undefined,
		repositoryName: "",
		secondaryColor: undefined,
		timeToExpiry: undefined,
		version: 0,
		whatsapp: false,
	})
	const [updateFormData, setUpdateFormData] = useState<BankState | undefined>(
		undefined
	)
	const [errors, setErrors] = useState(undefined)
	const [bankImgSrc, setBankImgSrc] = useState(undefined)

	const VisuallyHiddenInput = styled("input")({
		clip: "rect(0 0 0 0)",
		clipPath: "inset(50%)",
		height: 1,
		overflow: "hidden",
		position: "absolute",
		bottom: 0,
		left: 0,
		whiteSpace: "nowrap",
		width: 1,
	})

	useEffect(() => {
		if (isEditMode) {
			setFormData({
				autoAccept: bank?.autoAccept,
				bankContact: bank?.bankContact,
				bankId: bank?.bankId,
				bankImage: bank?.bankImage,
				bankName: bank?.bankName,
				checkIban: bank?.checkIban,
				createdBy: bank?.createdBy,
				creationDate: bank?.creationDate,
				deleted: bank?.deleted,
				enabled: bank?.enabled,
				id: bank?.id,
				modificationDate: bank?.modificationDate,
				modifiedBy: bank?.modifiedBy,
				primaryColor: bank?.primaryColor,
				repositoryName: bank?.repositoryName,
				secondaryColor: bank?.secondaryColor,
				timeToExpiry: bank?.timeToExpiry,
				version: bank?.version,
				whatsapp: bank?.whatsapp,
			})
		}
	}, [bank?.bankId, isEditMode])

	useEffect(() => {
		if (formData?.bankImage) {
			const base64Image = formData.bankImage
			if (formData.bankImage instanceof File) {
				return null
			}
			const newbankImgSrc = base64Image && atob(base64Image)
			setBankImgSrc(newbankImgSrc)
		}
	}, [formData?.bankImage])

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}))
		if (isEditMode) {
			setUpdateFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}
	}

	const handleColorChange = (value: string, name: string) => {
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}))
		if (isEditMode) {
			setUpdateFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}
	}

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, files } = event.target
		console.log(name)
		if (files?.[0]) {
			if (isEditMode) {
				setUpdateFormData((prevData) => ({
					...prevData,
					[name]: files[0],
				}))
			}

			const reader = new FileReader()
			reader.onloadend = () => {
				setBankImgSrc(reader.result)
				setFormData((prevData) => ({
					...prevData,
					[name]: btoa(reader.result),
				}))
			}
			reader.readAsDataURL(files[0])
		}
	}

	const handleSaveForm = () => {
		setIsSavingForm(true)
		dispatch(updateBank({ bankData: updateFormData, idBank: bank?.id }))
			.then((result: AxiosResponse) => {
				setIsSavingForm(false)
				if (result?.meta?.requestStatus === "rejected") {
					if (result?.payload?.status === 403) {
						setAlertMessage(
							t("unauthorized.error.alertMessage", { ns: "common" })
						)
						setIsErrorSnackBarOpen(true)
					} else if (result?.payload?.status === 400) {
						const backendErrors = result?.payload.data.reduce((acc, err) => {
							acc[err.field] = err.defaultMessage
							return acc
						}, {})
						setErrors(backendErrors)
					}
				} else {
					setIsSaved(true)
				}
			})
			.catch((error: AxiosError) => {
				if (error.response && error.response.status === 400) {
					const backendErrors = error.response.data.reduce((acc, err) => {
						acc[err.field] = err.defaultMessage
						return acc
					}, {})
					setErrors(backendErrors)
				} else if (error.response && error.response.status === 404) {
					setAlertMessage(
						t("unauthorized.error.alertMessage", { ns: "common" })
					)
					setIsErrorSnackBarOpen(true)
				}
			})
	}
	return (
		<>
			{isSaved ? (
				<Paper square elevation={0} sx={{ p: 4 }}>
					<Grid container>
						<Grid item>
							<SettingSvg width={"200px"} height={"200px"} />
						</Grid>
						<Grid item sx={{ alignContent: "center" }}>
							<Typography sx={{ fontWeight: "bold" }} component="div">
								{t(isEditMode ? "form.modified" : "form.created", {
									ns: "bank",
									bankName: formData?.bankName,
								})}
							</Typography>
						</Grid>
					</Grid>
				</Paper>
			) : (
				<Grid
					container
					flexDirection={"column"}
					justifyContent={"space-between"}
				>
					<Grid container gap={"16px"} flexDirection={"column"}>
						<Grid item sx={{ paddingBottom: "34px" }}>
							<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
								<TextField
									fullWidth
									InputLabelProps={{
										shrink: Boolean(
											updateFormData?.bankName || formData?.bankName
										),
									}}
									error={!!errors?.bankname}
									helperText={t(errors?.bankname, { ns: "bank" })}
									id="bankName"
									label={t("form.bankname", { ns: "bank" })}
									name="bankName"
									value={updateFormData?.bankName || formData?.bankName}
									onChange={handleInputChange}
									required
								/>
							</Grid>
							{/*
							<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
								<TextField
									id="timetoexpire"
									InputLabelProps={{
										shrink: Boolean(
											updateFormData?.timeToExpiry || formData?.timeToExpiry
										),
									}}
									label={t("form.timeExpire", { ns: "bank" })}
									name="timeToExpiry"
									value={
										updateFormData?.timeToExpiry ??
										parseInt(formData?.timeToExpiry) ??
										""
									}
									onChange={handleInputChange}
									type="number"
								/>
							</Grid>
							*/}
							<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
								<img
									alt={t("logo.bank", { ns: "common" })}
									style={{
										maxWidth: "50px",
										height: "fit-content",
										verticalAlign: "middle",
									}}
									src={bankImgSrc}
								/>
								<Button
									component="label"
									role={undefined}
									variant="contained"
									tabIndex={-1}
									startIcon={<UploadIcon />}
								>
									Cambia logo
									<VisuallyHiddenInput
										name="bankImage"
										type="file"
										onChange={(event) => handleFileChange(event)}
									/>
								</Button>
							</Grid>
							<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
								<MuiColorInput
									label={"Colore primario"}
									format="hex"
									value={formData?.primaryColor as MuiColorInputValue}
									onChange={(newValue) =>
										handleColorChange(newValue, "primaryColor")
									}
								/>
							</Grid>
							<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
								<MuiColorInput
									label={"Colore secondario"}
									format="hex"
									value={formData?.secondaryColor as MuiColorInputValue}
									onChange={(newValue) =>
										handleColorChange(newValue, "secondaryColor")
									}
								/>
							</Grid>
							{/*
							<Grid item flexGrow={1}>
								<FormControl>
									<FormLabel id="radio-auto-accept">
										{t("form.autoAccept", { ns: "bank" })}
									</FormLabel>
									<RadioGroup
										row
										aria-labelledby="radio-auto-accept"
										name="row-radio-auto-accept-group"
									>
										<FormControlLabel
											checked={Boolean(formData?.autoAccept)}
											control={<Radio />}
											label="Attivo"
										/>
										<FormControlLabel
											checked={Boolean(!formData?.autoAccept)}
											control={<Radio />}
											label="Disattivo"
										/>
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid item flexGrow={1}>
								<FormControl>
									<FormLabel id="radio-check-iban">
										{t("form.checkIban", { ns: "bank" })}
									</FormLabel>
									<RadioGroup
										row
										aria-labelledby="radio-check-iban"
										name="row-radio-check-iban-group"
									>
										<FormControlLabel
											checked={formData?.checkIban}
											control={<Radio />}
											label="Attivo"
										/>
										<FormControlLabel
											checked={!formData?.checkIban}
											control={<Radio />}
											label="Disattivo"
										/>
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid item flexGrow={1}>
								<FormControl>
									<FormLabel id="radio-whatsapp">Whatsapp</FormLabel>
									<RadioGroup
										row
										aria-labelledby="radio-whatsapp"
										name="row-radio-whatsapp-group"
									>
										<FormControlLabel
											checked={formData?.whatsapp}
											control={<Radio />}
											label="Attivo"
										/>
										<FormControlLabel
											checked={!formData?.whatsapp}
											control={<Radio />}
											label="Disattivo"
										/>
									</RadioGroup>
								</FormControl>
							</Grid>
							*/}
						</Grid>
					</Grid>
					<Grid
						item
						sx={{
							position: "fixed",
							width: "100%",
							bottom: "16px",
							backgroundColor: "#FFF",
							zIndex: 9,
						}}
					>
						{isSavingForm ? (
							<CircularProgress size="50px" />
						) : (
							<Button
								variant="contained"
								onClick={handleSaveForm}
								sx={{ margin: "16px 0" }}
							>
								{t("form.buttons.save", { ns: "bank" })}
							</Button>
						)}
					</Grid>
				</Grid>
			)}
			{!!alertMessage && (
				<Snackbar
					sx={{ width: "calc(200% - 50px)" }}
					anchorOrigin={{ vertical: "top", horizontal: "left" }}
					open={isErrorSnackBarOpen}
					autoHideDuration={30000}
					onClose={() => setIsErrorSnackBarOpen(false)}
				>
					<Alert
						onClose={() => setIsErrorSnackBarOpen(false)}
						severity="error"
						variant="filled"
						sx={{ width: "200%" }}
					>
						{alertMessage}
					</Alert>
				</Snackbar>
			)}
		</>
	)
}

export default CreateForm
